import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { cn } from "@/lib/utils";

/**
 * The subject as the field leaves it. The confirmation covers the subject, so
 * the words stored are not written again: the write would ask for it once more.
 */
function written(
  subject: string | null,
  draft: string,
  onSave: (subject: string) => Promise<unknown>,
): Promise<unknown> {
  const next = draft.trim();
  return next === (subject ?? "") ? Promise.resolve() : onSave(next);
}

/** The subject open for rewriting: the field, then Cancel and Save at the right. */
function SubjectEditor({
  subject,
  draft,
  busy,
  onDraft,
  onSave,
}: {
  subject: string | null;
  draft: string;
  busy: boolean;
  /** The field's words, or null to close it. */
  onDraft: (draft: string | null) => void;
  onSave: (subject: string) => Promise<unknown>;
}) {
  return (
    <>
      <Input
        size="dense"
        aria-label="Subject"
        value={draft}
        onChange={(event) => {
          onDraft(event.target.value);
        }}
        className="min-w-0 flex-1"
      />
      <Button
        type="button"
        variant="secondary"
        size="dense"
        onClick={() => {
          onDraft(null);
        }}
      >
        Cancel
      </Button>
      <Button
        type="button"
        variant="primary"
        size="dense"
        disabled={busy}
        onClick={() => {
          void written(subject, draft, onSave).then(
            () => {
              onDraft(null);
            },
            () => undefined,
          );
        }}
      >
        Save
      </Button>
    </>
  );
}

/**
 * The thing claimed, as the line above the element list, edited in place.
 *
 * It stands where the element that stated the whole invention used to, so no
 * element has to carry it and sit under every position. The confirmation of the
 * elements covers it, so saving it asks for the confirmation again.
 */
export function SubjectLine({
  subject,
  busy,
  onSave,
}: {
  subject: string | null;
  busy: boolean;
  /** Resolves when the subject is stored. A rejection leaves the field open. */
  onSave: (subject: string) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // A card of its own over the elements, its acts at the right as the
  // element cards set theirs. The design has no subject line; this is where
  // the thing claimed is named, which the later stages read.
  return (
    <Panel
      data-testid="invention-subject"
      shape="row"
      className="mt-4 flex flex-wrap items-center gap-3 text-[12.5px]"
    >
      <MicroLabel size="md">Subject</MicroLabel>
      {draft === null ? (
        <>
          <span
            className={cn(
              "min-w-0 flex-1",
              subject === null
                ? "text-muted-foreground"
                : "text-[13px] font-semibold",
            )}
          >
            {subject ?? "No subject yet: name the thing claimed"}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="dense"
            aria-label="Edit subject"
            onClick={() => {
              setDraft(subject ?? "");
            }}
          >
            Edit
          </Button>
        </>
      ) : (
        <SubjectEditor
          subject={subject}
          draft={draft}
          busy={busy}
          onDraft={setDraft}
          onSave={onSave}
        />
      )}
    </Panel>
  );
}
